<template>
  <div class="w-full mt-4 md:mt-6 lg:mt-8 space-y-6 md:space-y-8">

    <!-- ── Skeleton: shown while initial data loads ── -->
    <template v-if="!finishedLoadingInitialData">

      <!-- Skeleton: Announcements -->
      <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse">
        <div class="tw-flex items-center justify-between mb-4">
          <div class="h-4 w-36 bg-gray-200 rounded"/>
          <div class="h-3 w-12 bg-gray-200 rounded"/>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-for="i in 2" :key="i" class="rounded-xl border border-gray-100 overflow-hidden">
            <div class="h-32 bg-gray-200"/>
            <div class="p-4 space-y-2">
              <div class="h-3 w-3/4 bg-gray-200 rounded"/>
              <div class="h-3 w-1/2 bg-gray-200 rounded"/>
              <div class="mt-3 pt-3 border-t border-gray-100 tw-flex gap-4">
                <div class="h-3 w-16 bg-gray-200 rounded"/>
                <div class="h-3 w-16 bg-gray-200 rounded"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Skeleton: Messages -->
      <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse">
        <div class="tw-flex items-center justify-between mb-4">
          <div class="h-4 w-32 bg-gray-200 rounded"/>
          <div class="h-3 w-12 bg-gray-200 rounded"/>
        </div>
        <div class="divide-y divide-gray-100">
          <div v-for="i in 3" :key="i" class="tw-flex items-center gap-3 py-3 px-2">
            <div class="w-8 h-8 rounded-full bg-gray-200 shrink-0"/>
            <div class="flex-1">
              <div class="tw-flex justify-between mb-1.5">
                <div class="h-3 w-24 bg-gray-200 rounded"/>
                <div class="h-3 w-10 bg-gray-200 rounded"/>
              </div>
              <div class="h-2.5 w-3/4 bg-gray-200 rounded"/>
            </div>
          </div>
        </div>
      </div>

      <!-- Skeleton: Communities -->
      <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse" v-if="this.$store.getters.hasAccessToCommunities">
        <div class="tw-flex items-center justify-between mb-4">
          <div class="h-4 w-28 bg-gray-200 rounded"/>
          <div class="h-3 w-12 bg-gray-200 rounded"/>
        </div>
        <div class="grid gap-3" style="grid-template-columns: repeat(auto-fill, minmax(180px, 1fr))">
          <div v-for="i in 3" :key="i" class="rounded-[10px] border border-gray-100 overflow-hidden">
            <div class="h-[120px] bg-gray-200"/>
            <div class="p-3 space-y-2">
              <div class="h-3 w-3/4 bg-gray-200 rounded"/>
              <div class="h-2.5 w-full bg-gray-200 rounded"/>
              <div class="h-2.5 w-2/3 bg-gray-200 rounded"/>
            </div>
          </div>
        </div>
      </div>

    </template>

    <!-- ── Transition Pulse ── -->
    <div
      v-if="(isUserOfDataScoutsTeam || canAccessReports) && $store.getters.isFoodleap"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">Transition Pulse</h3>
      </div>

      <div v-if="transitionPulseMonths.length > 0" class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="entry in transitionPulseMonths.slice(0, 6)"
          :key="`${entry.year}-${entry.month}`"
          class="group cursor-pointer rounded-xl border border-gray-200 bg-white p-4 transition-all hover:shadow-md hover:border-primary/30"
          @click="$router.push(`/transition-pulse/${entry.year}/${entry.month}`)"
        >
          <!-- Month label -->
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-1 m-0">
            {{ formatPulseMonth(entry.year, entry.month) }}
          </p>
          <h4 class="text-sm font-bold text-gray-900 m-0 mb-3">Transition Pulse</h4>

          <!-- Category pills -->
          <div v-if="entry.categories && entry.categories.length" class="tw-flex flex-wrap gap-1 mb-3">
            <span
              v-for="cat in entry.categories"
              :key="cat"
              class="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium capitalize"
            >
              {{ cat }}
            </span>
          </div>

          <!-- Read link -->
          <div class="tw-flex items-center gap-1 text-xs font-medium text-primary opacity-80 group-hover:opacity-100 transition-opacity">
            Read
            <svg class="w-3 h-3 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </div>
        </div>
      </div>

      <div v-else class="tw-flex flex-col items-center justify-center py-7 px-4 text-center ph-empty-anim">
        <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--float">
          <Icon name="wave-square" size="large"/>
        </div>
        <p class="text-sm text-gray-500 m-0 ph-empty-anim-text">No Transition Pulses available yet</p>
      </div>
    </div>

    <!-- ── Innovation Radar ── -->
    <div
      v-if="(isUserOfDataScoutsTeam || canAccessReports) && $store.getters.isFoodleap"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">Innovation Radar</h3>
      </div>

      <div v-if="innovationRadarQuarters.length > 0" class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="entry in innovationRadarQuarters.slice(0, 6)"
          :key="`${entry.year}-${entry.quarter}`"
          class="group cursor-pointer rounded-xl border border-gray-200 bg-white p-4 transition-all hover:shadow-md hover:border-primary/30"
          @click="$router.push(`/innovation-radar/${entry.id}`)"
        >
          <!-- Quarter label -->
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-1 m-0">
            Q{{ entry.quarter }} {{ entry.year }}
          </p>
          <h4 class="text-sm font-bold text-gray-900 m-0 mb-3">{{ entry.title || 'Innovation Radar' }}</h4>

          <!-- Mini radar preview dots -->
          <div class="relative w-full rounded-full border border-gray-100 mb-3 overflow-hidden bg-gray-50" style="padding-top: 100%">
            <div class="absolute inset-[25%] rounded-full border border-gray-100"/>
            <div class="absolute bg-gray-200" style="top:50%;left:0;right:0;height:1px"/>
            <div class="absolute bg-gray-200" style="left:50%;top:0;bottom:0;width:1px"/>
            <div
              v-for="(dot, i) in entry.previewDots || []"
              :key="i"
              class="absolute rounded-full"
              :style="{
                left: dot.x + '%',
                top: (100 - dot.y) + '%',
                width: '6px',
                height: '6px',
                backgroundColor: dot.color,
                transform: 'translate(-50%, -50%)',
              }"
            />
          </div>

          <!-- Actor count + read link -->
          <div class="tw-flex items-center justify-between">
            <span class="text-xs text-gray-400">{{ entry.actor_count }} actors</span>
            <div class="tw-flex items-center gap-1 text-xs font-medium text-primary opacity-80 group-hover:opacity-100 transition-opacity">
              View
              <svg class="w-3 h-3 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="tw-flex flex-col items-center justify-center py-7 px-4 text-center ph-empty-anim">
        <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--float">
          <Icon name="location-marker" size="large"/>
        </div>
        <p class="text-sm text-gray-500 m-0 ph-empty-anim-text">No Innovation Radars available yet</p>
      </div>
    </div>

    <!-- Announcements Section -->
    <div v-if="announcements.length > 0 && $store.getters.hasAccessToCommunities" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0" v-if="joinedCommunities.length > 1">
          {{ $t('personal_home_latest_announcements', { announcements: announcementsLabel.toLowerCase() }) }}
        </h3>
        <h3 class="text-base font-semibold text-gray-900 m-0" v-else>
          {{ $t('personal_home_latest_announcements', { announcements: announcementsLabel.toLowerCase(), community: joinedCommunities[0].name }) }}
        </h3>
        <router-link to="/announcements" class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary"
                     @click="trackViewAll('announcements')">
          {{ $t('view_all') }}
          <Icon name="arrow-right" size="14"/>
        </router-link>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PersonalHomeAnnouncementCard
          v-for="announcement in announcements"
          :key="announcement.id"
          :announcement="announcement"
          @open="openAnnouncementModal(announcement)"
        />
      </div>

      <PersonalHomeAnnouncementModal
        v-if="activeAnnouncement"
        v-model="announcementModalOpen"
        :announcement="activeAnnouncement"
      />
    </div>

    <!-- Recent Messages Section -->
    <div v-if="missedMessages.length > 0" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('personal_home_recent_messages') }}</h3>
        <router-link to="/profile/conversations"
                     class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary"
                     @click="trackViewAll('messages')">
          {{ $t('view_all') }}
          <Icon name="arrow-right" size="14"/>
        </router-link>
      </div>
      <div class="divide-y divide-gray-100">
        <div
          v-for="message in missedMessages"
          :key="message.id"
          class="tw-flex items-center gap-3 py-3 px-2 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
          @click="openConversation(message.conversationId, message.sender.id)"
        >
          <div class="shrink-0">
            <AvatarNew :user="message.sender"/>
          </div>
          <div class="min-w-0 flex-1">
            <div class="tw-flex items-baseline justify-between gap-2 mb-0.5">
              <span class="text-sm font-semibold text-gray-900 truncate">{{ message.sender.name }}</span>
              <span class="text-xs text-gray-400 shrink-0">{{ formatMessageTime(message.sendAt) }}</span>
            </div>
            <p class="text-xs text-gray-500 truncate m-0">{{ message.message }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Community Members Section -->
    <div v-if="latestCommunityMembers.length > 0 && this.$store.getters.hasAccessToCommunities" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('personal_home_new_community_members') }}</h3>
        <!--        <router-link to="/communities" class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary">
                  {{ $t('view_all') }}
                  <Icon name="arrow-right" size="14"/>
                </router-link>-->
      </div>
      <div class="space-y-3 md:space-y-4">
        <CommunityMemberCard
          v-for="member in latestCommunityMembers"
          :key="member.id"
          :member="member"
          :compact="false"
          :show-actions="true"
          :show-organization="true"
          :show-organization-link="true"
          :show-department="true"
          :show-experience="false"
        />
      </div>
    </div>

    <!-- ── My Communities ── -->
    <div
      v-if="$store.getters.hasAccessToCommunities && (joinedCommunities.length || suggestedCommunities.length)"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <!-- Block header -->
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('personal_home_my_communities') }}</h3>
        <router-link to="/communities" class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary"
                     @click="trackViewAll('communities')">
          {{ $t('view_all') }}
          <Icon name="arrow-right" size="14"/>
        </router-link>
      </div>

      <!-- Joined communities -->
      <div v-if="joinedCommunities.length" class="ph-communities-rail">
        <PersonalHomeCommunityCard
          v-for="community in joinedCommunities.slice(0, 3)"
          :key="community.id"
          :community="community"
          @click="viewCommunity(community)"
        />
      </div>
      <!-- Empty: no joined communities -->
      <div v-else class="tw-flex flex-col items-center justify-center py-10 px-4 text-center ph-empty-anim">
        <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--pulse">
          <Icon name="users" size="large"/>
        </div>
        <p class="text-sm text-gray-500 m-0 mb-2.5 ph-empty-anim-text">{{ $t('profile_no_communities_joined') }}</p>
        <DsButton
          variant="rounded"
          to="/communities"
          label="Browse communities"
          size="small"
        />
      </div>

      <!-- Suggested divider + row -->
      <template v-if="suggestedCommunities.length">
        <div class="tw-flex items-center gap-3 my-4">
          <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 whitespace-nowrap">{{ $t('personal_home_communities_suggested') }}</span>
          <div class="flex-1 h-px bg-gray-200"/>
        </div>

        <div class="ph-communities-rail ph-communities-rail--suggested">
          <PersonalHomeCommunityCard
            v-for="community in suggestedCommunities"
            :key="community.id"
            :community="community"
            variant="suggested"
            @click="$router.push('/communities/' + community.id)"
          />
        </div>
      </template>
    </div>

    <!-- ── My Spotting Areas ── -->
    <div
      v-if="$store.getters.hasAccessToNewSpottingAreas"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ spottingAreasLabel }}</h3>
        <router-link to="/spotting-areas" class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary"
                     @click="trackViewAll('spotting-areas')">
          {{ $t('view_all') }}
          <Icon name="arrow-right" size="14"/>
        </router-link>
      </div>
      <div v-if="joinedSpottingAreas.length" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="area in joinedSpottingAreas.slice(0, 6)"
          :key="area.id"
          class="group cursor-pointer overflow-hidden rounded-lg border border-gray-200 transition-all hover:shadow-md"
          @click="viewSpottingArea(area)"
        >
          <div class="relative h-40 overflow-hidden bg-gray-100">
            <img
              v-if="area.image"
              :src="getImageForSpottingArea(area)"
              :alt="area.name"
              class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
            <div class="absolute bottom-3 left-3 right-3">
              <h5 class="font-bold text-white text-sm uppercase tracking-wide truncate">{{ area.name }}</h5>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="tw-flex flex-col items-center justify-center py-7 px-4 text-center ph-empty-anim">
        <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--float">
          <Icon name="location-marker" size="large"/>
        </div>
        <p class="text-sm text-gray-500 m-0 ph-empty-anim-text">{{ $t('profile_no_spotting_areas_access') }}</p>
      </div>
    </div>

    <!-- ── My Actors ── -->
    <div
      v-if="actorClaims.length || showAddActorButton"
      id="my-actors-section"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('profile_settings_my_actors', { actors: actorsLabel }) }}</h3>
        <DsButton
          variant="rounded"
          v-if="showAddActorButton"
          @click="showCreateActorModal"
          icon="plus"
          size="small"
          :label="$t('add_actor_add_button', { actor: actorLabel })"
        />
      </div>
      <div>
        <div v-if="!actorClaims.length" class="tw-flex flex-col items-center justify-center py-7 px-4 text-center ph-empty-anim">
          <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--bounce">
            <Icon name="building" size="large"/>
          </div>
          <p class="text-sm text-gray-500 m-0 mb-2.5 ph-empty-anim-text">No actors claimed yet</p>
          <DsButton
            v-if="showAddActorButton"
            @click="showCreateActorModal"
            variant="rounded"
            size="small"
            icon="plus"
            :label="$t('add_actor_add_button', { actor: actorLabel})"
          />
        </div>
        <div v-else class="grid gap-4 md:grid-cols-2">
          <div
            v-for="actor in actorClaims"
            :key="actor.id"
            class="overflow-hidden rounded-lg border border-gray-200 bg-white transition-shadow hover:shadow-md"
          >
            <div class="p-4">
              <router-link :to="'/actors/' + actor.id" class="tw-flex items-start gap-3">
                <div class="shrink-0">
                  <ActorAvatar :actor="actor" width="40"/>
                </div>
                <div class="min-w-0 flex-1">
                  <h5 class="font-semibold text-gray-900 text-sm truncate mb-0.5">{{ actor.name }}</h5>
                </div>
              </router-link>
              <p v-if="actor.short_description" class="mt-3 line-clamp-2 text-sm text-gray-500 leading-relaxed">
                {{ actor.short_description }}
              </p>
            </div>
            <div class="border-t border-gray-100 bg-gray-50 px-4 py-3">
              <div class="tw-flex items-center justify-between mb-2">
                <span class="text-xs text-gray-400">{{ $t('personal_home_weekly_trend') }}</span>
                <div
                  :class="['tw-flex items-center gap-1 text-xs font-medium',
                    actorTrend(actor.id) === 'up' ? 'text-green-600' :
                    actorTrend(actor.id) === 'down' ? 'text-red-500' : 'text-gray-400']"
                >
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          :d="actorTrend(actor.id) === 'down'
                        ? 'M13 17H5m0 0v-8m0 8l8-8 4 4 6-6'
                        : actorTrend(actor.id) === 'up'
                          ? 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                          : 'M5 12h14'"
                    />
                  </svg>
                  {{
                    actorTrend(actor.id) === 'up' ? $t('personal_home_actor_trend_growing') : actorTrend(actor.id) === 'down' ? $t('personal_home_actor_trend_declining') : $t('personal_home_actor_trend_stable')
                  }}
                </div>
              </div>
              <div class="tw-flex items-end gap-px h-8">
                <div
                  v-for="(bar, i) in actorBarData(actor.id)"
                  :key="i"
                  class="flex-1 rounded-t bg-primary hover:bg-primary transition-colors"
                  :style="{ height: bar + '%' }"
                  v-tooltip.top="$t('personal_home_views') + ': ' + actorBarData(actor.id, true)[i]"
                />
              </div>
            </div>
          </div>
        </div><!-- end v-else grid -->
      </div>
    </div>

    <!-- ── My Products ── -->
    <div
      v-if="productClaims.length || showAddProductButton"
      id="my-products-section"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('profile_settings_my_products', { products: productsLabel }) }}</h3>
        <DsButton
          variant="rounded"
          v-if="showAddProductButton"
          @click="showAddProductModal"
          icon="plus"
          size="small"
          :label="$t('add_product_add_button', { product: productLabel })"
        />
      </div>
      <div>
        <div v-if="!productClaims.length" class="tw-flex flex-col items-center justify-center py-7 px-4 text-center ph-empty-anim">
          <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--bounce">
            <Icon name="icons" size="large"/>
          </div>
          <p class="text-sm text-gray-500 m-0 mb-2.5 ph-empty-anim-text">{{ $t('profile_no_products', { actors: productsLabel }) }}</p>
          <DsButton
            v-if="showAddProductButton"
            @click="showAddProductModal"
            variant="rounded"
            size="small"
            icon="plus"
            :label="$t('add_product_add_button', {product: productLabel})"
          />
        </div>
        <div v-else class="grid gap-4 md:grid-cols-2">
          <div
            v-for="product in productClaims"
            :key="product.id"
            class="overflow-hidden rounded-lg border border-gray-200 bg-white transition-shadow hover:shadow-md"
          >
            <div class="p-4">
              <router-link :to="'/actors/' + product.id" class="tw-flex items-start gap-3">
                <div class="shrink-0">
                  <ActorAvatar :actor="product" width="40"/>
                </div>
                <div class="min-w-0 flex-1">
                  <h5 class="font-semibold text-gray-900 text-sm truncate mb-0.5">{{ product.name }}</h5>
                </div>
              </router-link>
              <p v-if="product.short_description" class="mt-3 line-clamp-2 text-sm text-gray-500 leading-relaxed">
                {{ product.short_description }}
              </p>
            </div>
            <div class="border-t border-gray-100 bg-gray-50 px-4 py-3">
              <div class="tw-flex items-center justify-between mb-2">
                <span class="text-xs text-gray-400">{{ $t('personal_home_weekly_trend') }}</span>
                <div
                  :class="['tw-flex items-center gap-1 text-xs font-medium',
                    actorTrend(product.id) === 'up' ? 'text-green-600' :
                    actorTrend(product.id) === 'down' ? 'text-red-500' : 'text-gray-400']"
                >
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          :d="actorTrend(product.id) === 'down'
                        ? 'M13 17H5m0 0v-8m0 8l8-8 4 4 6-6'
                        : actorTrend(product.id) === 'up'
                          ? 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                          : 'M5 12h14'"
                    />
                  </svg>
                  {{
                    actorTrend(product.id) === 'up' ? $t('personal_home_actor_trend_growing') : actorTrend(product.id) === 'down' ? $t('personal_home_actor_trend_declining') : $t('personal_home_actor_trend_stable')
                  }}
                </div>
              </div>
              <div class="tw-flex items-end gap-px h-8">
                <div
                  v-for="(bar, i) in actorBarData(product.id)"
                  :key="i"
                  class="flex-1 rounded-t bg-primary hover:bg-primary transition-colors"
                  :style="{ height: bar + '%' }"
                  v-tooltip.top="$t('personal_home_views') + ': ' + actorBarData(product.id, true)[i]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Testimonials ── -->
    <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div class="tw-flex items-center justify-between mb-5">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('testimonials_title') }}</h3>
        <ds-button
          v-if="actorClaims.length > 0"
          icon="plus"
          :label="$t('write_testimonial')"
          variant="rounded"
          size="small"
          @click="openWriteTestimonialModal"
        />
      </div>

      <!-- Empty state -->
      <div
        v-if="testimonials?.given?.length === 0 && testimonials?.received?.length === 0"
        class="tw-flex flex-col items-center justify-center py-10 text-center ph-empty-anim"
      >
        <div class="w-12 h-12 rounded-full bg-gray-100 tw-flex items-center justify-center mb-3 ph-empty-state__icon-wrap ph-empty-state__icon-wrap--bounce">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
          </svg>
        </div>
        <p class="text-sm text-gray-500 m-0 ph-empty-anim-text">{{ $t('no_testimonials') }}</p>
        <ds-button
          v-if="actorClaims.length > 0"
          icon="plus"
          :label="$t('write_testimonial')"
          variant="rounded"
          size="small"
          class="mt-3"
          @click="openWriteTestimonialModal"
        />
      </div>

      <div v-else class="space-y-6">
        <!-- Sent -->
        <div v-if="testimonials?.given?.length > 0">
          <div class="tw-flex items-center gap-2 mb-4">
            <span class="text-xs font-semibold uppercase tracking-widest text-primary whitespace-nowrap">Sent</span>
            <div class="flex-1 h-px bg-gray-100"/>
          </div>
          <div v-for="group in testimonials.given" :key="group.target_actor?.id" class="mb-5">
            <a
              :href="'/actors/' + group.target_actor?.elastic_id"
              class="inline-flex items-center gap-2 mb-3 !no-underline group"
            >
              <ActorAvatar :actor="group.target_actor" width="24"/>
              <span class="text-xs font-semibold text-gray-600 group-hover:text-primary transition-colors">{{ group.target_actor?.name }}</span>
            </a>
            <div class="grid md:grid-cols-2 gap-3">
              <div
                v-for="testimonial in group.testimonials"
                :key="testimonial.id"
                class="relative bg-gray-50 rounded-xl p-4 border border-gray-100 tw-flex flex-col"
              >
                <svg class="absolute top-3 right-3 h-5 w-5 text-gray-200" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
                <p class="text-sm text-gray-700 leading-relaxed italic mb-0 pr-6 flex-1">"{{ testimonial.content }}"</p>
                <div class="tw-flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
                  <div class="tw-flex items-center gap-1.5">
                    <ActorAvatar :actor="testimonial.author_actor" width="20"/>
                    <span class="text-xs font-medium text-gray-500">{{ testimonial.author_actor?.name }}</span>
                  </div>
                  <span class="text-[10px] text-gray-400 shrink-0">{{ formatMessageTime(testimonial.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Received -->
        <div v-if="testimonials?.received?.length > 0">
          <div class="tw-flex items-center gap-2 mb-4">
            <span class="text-xs font-semibold uppercase tracking-widest text-primary whitespace-nowrap">Received</span>
            <div class="flex-1 h-px bg-gray-100"/>
          </div>
          <div v-for="group in testimonials.received" :key="group.target_actor?.id" class="mb-5">
            <a
              :href="'/actors/' + group.target_actor?.elastic_id"
              class="inline-flex items-center gap-2 mb-3 !no-underline group"
            >
              <ActorAvatar :actor="group.target_actor" width="24"/>
              <span class="text-xs font-semibold text-gray-600 group-hover:text-primary transition-colors">{{ group.target_actor?.name }}</span>
            </a>
            <div class="grid md:grid-cols-2 gap-3">
              <div
                v-for="testimonial in group.testimonials"
                :key="testimonial.id"
                class="relative bg-gray-50 rounded-xl p-4 border border-gray-100 tw-flex flex-col"
              >
                <svg class="absolute top-3 right-3 h-5 w-5 text-gray-200" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
                <p class="text-sm text-gray-700 leading-relaxed italic mb-0 pr-6 flex-1">"{{ testimonial.content }}"</p>
                <div class="tw-flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
                  <div class="tw-flex items-center gap-1.5">
                    <ActorAvatar :actor="testimonial.author_actor" width="20"/>
                    <span class="text-xs font-medium text-gray-500">{{ testimonial.author_actor?.name }}</span>
                  </div>
                  <span class="text-[10px] text-gray-400 shrink-0">{{ formatMessageTime(testimonial.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import Tabs from '../Tabs/Tabs.vue'
  import Avatar from '../Avatar/Avatar.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import Dropdown from '../Dropdown/Dropdown.vue'
  import UiMixin from '../../util/UiMixin.js'
  import IntroJsMixin from '../../util/IntroJsMixin.js'

  import ToggleButton from '../Form/ToggleButton.vue'
  import { fetchLatestConversation } from '@/api/conversations'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '@/store/modules/filters'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import { fetchLatestMembersForUser } from '@/api/communities'
  import { getActorWeeklyTrend, getUserPreferences } from '@/api/user'
  import { updateActor } from '@/api/actors'
  import CommunityMemberCard from '@/components/Communities/CommunityMemberCard.vue'
  import ActorMiniCard from '@/components/ActorMiniCard/ActorMiniCard.vue'
  import ChallengeCardNew from '@/components/Challenges/ChallengeCardNew.vue'
  import AnnouncementCard from '@/components/Simplified/AnnouncementCard.vue'
  import ActorInfoCard from '@/components/ActorInfo/ActorInfoCard.vue'
  import SimplifiedImageCard from '@/components/Connection/SimplifiedImageCard.vue'
  import ProfileCardsSection from '@/components/Profile/ProfileCardsSection.vue'
  import AnnouncementOrResourceCard from '@/components/Simplified/AnnouncementOrResourceCard.vue'
  import SimpleArticlePreview from '@/components/SimpleArticlePreview.vue'
  import { getImageForAnnouncement, getImageForCommunity, getImageForSpottingArea } from '@/util/helpers'
  import SimplifiedAnnouncementPreview from '@/components/Simplified/SimplifiedAnnouncementPreview.vue'
  import CommunityMixin from '@/util/CommunityMixin'
  import SpottingAreaMixin from '@/util/SpottingAreaMixin'
  import { actorLogo } from '@/util/actor'
  import { ACTION_TYPES as NOTIFICATION_ACTION_TYPES } from '@/store/modules/notifications'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import { getUserTestimonials } from '@/Domain/Actor/Api/testimonials'
  import { fetchUserLatestCommunityAnnouncements } from '@/Domain/Announcements/Api/notifications'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'
  import PersonalHomeCommunityCard from '@/components/Profile/PersonalHomeCommunityCard.vue'
  import PersonalHomeAnnouncementCard from '@/components/Profile/PersonalHomeAnnouncementCard.vue'
  import PersonalHomeAnnouncementModal from '@/components/Profile/PersonalHomeAnnouncementModal.vue'
  import { fetchTransitionPulseMonths } from '@/api/transition-pulse'
  import { fetchInnovationRadarQuarters } from '@/api/innovation-radar'

  export default {
    name: 'PersonalHomeContent',
    data () {
      return {
        componentKey: 1,
        portfolios: [],
        finishedLoadingInitialData: false,
        conceptSearches: [],
        availableResources: [],
        relevantTags: [],
        acceptedTags: [],
        relevantTaxonomies: [],
        acceptedTaxonomies: [],
        updatingAllowPersonalisedContentOption: false,
        missedMessages: [],
        latestCommunityMembers: [],
        newSpottingAreaActors: [],
        challenges: [],
        announcements: [],
        activeAnnouncement: null,
        announcementModalOpen: false,
        userPreferences: {
          preferredSpottingAreaId: null,
          preferredCommunityId: null,
        },
        highlightedSection: null,
        featuredNotifications: [],
        nonFeaturedAnnouncements: [],
        actorWeeklyTrendData: {},
        testimonials: [],
        transitionPulseMonths: [],
        innovationRadarQuarters: [],
      }
    },
    computed: {
      canAccessReports () {
        // iedereen met foodvalley.nl email & Julie lietaer van Ellie, zit ook op foodleap.
        if (['julie.lietaer@ariadne-innovation.com', 'vansteenlandt.jan@gmail.com'].includes(this.profile.email)) {
          return true
        }

        return this.profile.email.endsWith('@foodvalley.nl')
      },
      profile () {
        return this.$store.state.user.profile
      },
      hasAnyData () {
        return this.missedMessages.length > 0 ||
          this.latestCommunityMembers.length > 0 ||
          this.newSpottingAreaActors.length > 0 ||
          this.challenges.length > 0 ||
          this.announcements.length > 0
      },
      showPreferencesGuidance () {
        // Show guidance if user hasn't set preferences and has access to communities or spotting areas
        const hasNoCommunityPreference = !this.userPreferences.preferredCommunityId && this.hasJoinedCommunities
        const hasNoSpottingAreaPreference = !this.userPreferences.preferredSpottingAreaId && this.hasAccessToSpottingAreas

        return hasNoCommunityPreference || hasNoSpottingAreaPreference
      },
      hasJoinedCommunities () {
        return this.profile && this.profile.joinedCommunityIds && this.profile.joinedCommunityIds.length > 0
      },
      hasAccessToSpottingAreas () {
        // Check if user has access to monitoring and has accessible spotting areas
        return this.$store.getters.hasAccessToMonitoring &&
          this.profile &&
          this.profile.accessibleSpottingAreas &&
          this.profile.accessibleSpottingAreas.length > 0
      },
      actorClaims () {
        if (!this.profile?.claims) return []
        return this.profile.claims.filter(c => c.actor_type !== 'Product')
      },
      productClaims () {
        if (!this.profile?.claims) return []
        return this.profile.claims.filter(c => c.actor_type === 'Product')
      },
      showAddProductButton () {
        if (this.$store.getters.isMember) return true
        return this.$store.getters.userCanCreate &&
          this.$store.getters.claimableActorTypes?.includes('Product')
      },
      showAddActorButton () {
        if (this.$store.getters.isMember) return true
        return this.$store.getters.userCanCreate &&
          this.$store.getters.claimableActorTypes?.length > 0
      },
      suggestedCommunities () {
        const joinedIds = (this.joinedCommunities || []).map(c => c.id)
        return (this.$store.state.communities.listData.data || [])
          .filter(c => !joinedIds.includes(c.id))
          .slice(0, 3)
      },
      getPreferredCommunityName () {
        let community = this.communities.filter((community) => {
          return community.id === this.getPreferredCommunityId()
        })

        if (community.length > 0) {
          return community[0].name
        }

        return null
      },
    },
    methods: {
      actorLogo,
      getImageForCommunity,
      trackViewAll (section) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.VIEW_ALL, section)
      },
      getImageForSpottingArea,
      openAnnouncementModal (announcement) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.OPEN_ANNOUNCEMENT, announcement.title)
        this.activeAnnouncement = announcement
        this.announcementModalOpen = true
      },
      viewCommunity (community) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.VIEW_COMMUNITY, community.name)
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$store.commit(FILTERS_MUTATION_TYPES.SET_PORTFOLIO, community.portfolioId)
        this.$router.push('/communities/' + community.id)
      },
      viewSpottingArea (spottingArea) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.VIEW_SPOTTING_AREA, spottingArea.name)
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$router.push('/spotting-areas/' + spottingArea.id)
      },
      openWriteTestimonialModal () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {})
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CREATE_TESTIMONIAL)
      },
      showCreateActorModal () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.ADD_ACTOR)
        if (this.$store.getters.isPublisherEnabled) {
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { forceClaim: true })
        }
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
      },
      showAddProductModal () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.ADD_PRODUCT)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_PRODUCT_FROM_PROFILE)
      },
      linkProductToParent (product) {
        const parentId = product.parentId
        if (!parentId) return

        const parentActor = this.actorClaims.find(a => a.id === parentId)
        if (!parentActor) return

        const hasProduct = [...(parentActor.has_product || []), {
          to: product.id,
          to_name: product.name,
          start: null,
          end: null,
        }]

        updateActor({ id: parentId, data: { has_product: hasProduct } })
      },
      actorBarData (actorId, actualNumber = false) {
        const trend = this.actorWeeklyTrendData[actorId]
        if (!trend) return Array(6).fill(0)
        const views = trend.dailyViews

        // Aggregate 30 daily values into 6 buckets of 5 days each
        const bucketSize = Math.ceil(views.length / 6)
        const buckets = Array.from({ length: 6 }, (_, i) => {
          const slice = views.slice(i * bucketSize, (i + 1) * bucketSize)
          return slice.reduce((a, b) => a + b, 0)
        })

        if (actualNumber) {
          return buckets
        }

        const max = Math.max(...buckets, 1)
        return buckets.map(v => Math.max(4, Math.round((v / max) * 100)))
      },
      actorTrend (actorId) {
        return this.actorWeeklyTrendData[actorId]?.trend || 'neutral'
      },
      async fetchActorWeeklyTrend () {
        try {
          this.actorWeeklyTrendData = await getActorWeeklyTrend()
        } catch (e) { /* silently ignore */
        }
      },
      async fetchUserTestimonials () {
        await getUserTestimonials()
          .then((response) => {
            this.testimonials = response
          })
      },
      async fetchTransitionPulse () {
        // TODO: replace with real API call once backend is ready
        try {
          this.transitionPulseMonths = await fetchTransitionPulseMonths()
        } catch (e) { /* silently ignore */
        }
      },
      formatPulseMonth (year, month) {
        return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      },
      async fetchInnovationRadar () {
        const colors = ['#4f6f9f', '#5fbf7f', '#d28b4d', '#5f7f5f', '#9b59b6', '#9ca3af']

        const makeDots = (seed) => Array.from({ length: 18 }, (_, i) => ({
          x: ((seed * 7 + i * 17 + i * i * 3) % 80) + 10,
          y: ((seed * 13 + i * 11 + i * i * 5) % 80) + 10,
          color: colors[i % colors.length],
        }))
        // TODO: replace with real API call once backend is ready
        // try {
        await fetchInnovationRadarQuarters()
          .then((response) => {
            this.innovationRadarQuarters = response.map((radar, index) => {
              return {
                ...radar,
                previewDots: makeDots(index)
              }
            })
          })

        console.log(this.innovationRadarQuarters)
      },
      getImageForAnnouncement,
      async loadUserPreferences () {
        try {
          const response = await getUserPreferences()
          this.userPreferences = response.preferences
        } catch (error) {
          console.error('Error loading user preferences:', error)
        }
      },
      getPreferredCommunityId () {
        // Use preferred community if set, otherwise fall back to first available
        if (this.userPreferences.preferredCommunityId) {
          return this.userPreferences.preferredCommunityId
        }
        if (this.profile && this.profile.joinedCommunityIds && this.profile.joinedCommunityIds.length > 0) {
          return this.profile.joinedCommunityIds[0]
        }
        return null
      },
      getPreferredSpottingAreaId () {
        // Use preferred spotting area if set, otherwise fall back to first available
        if (this.userPreferences.preferredSpottingAreaId) {
          return this.userPreferences.preferredSpottingAreaId
        }
        if (this.profile && this.profile.accessibleSpottingAreas && this.profile.accessibleSpottingAreas.length > 0) {
          return this.profile.accessibleSpottingAreas[0]
        }
        return null
      },
      handleClickActorCard (actor) {
        const spottingAreaId = this.getPreferredSpottingAreaId()
        this.$router.push(`/spotting-areas/${spottingAreaId}/actors/${actor.id}`)
      },
      openConversation (conversationId, userId) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.OPEN_CONVERSATION, conversationId)
        this.$router.push('/profile/conversations/' + conversationId).then(() => {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
            component: 'user-information-side-panel',
            metaData: {
              userId: userId,
              allowDelete: false,
            },
          })
        })
      },
      fetchLatestConversationMessages () {
        return fetchLatestConversation()
          .then((response) => {
            this.missedMessages = response
          })
      },
      fetchLatestCommunityMembers () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          return fetchLatestMembersForUser()
            .then((response) => {
              this.latestCommunityMembers = response.filter((user) => {
                return user.id !== this.profile.id
              })
            })
        }
        return Promise.resolve()
      },
      fetchLatestAnnouncements () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          return fetchUserLatestCommunityAnnouncements()
            .then((response) => {
              this.announcements = response
            })
            .catch((error) => {
              console.error('Error fetching announcements:', error)
            })
        }
      },
      formatMessageTime (timestamp) {
        return new Date(timestamp).toLocaleDateString()
      },
      async resetUserObjects () {
        // Refresh data when user profile is updated
        await this.loadUserPreferences()
        await this.fetchLatestCommunityMembers()
        await this.fetchLatestAnnouncements()
      },
    },
    created () {
      this.$bus.on('updateUserProfile', this.resetUserObjects)
      this.$bus.on('productAddedViaOverlay', this.linkProductToParent)
    },
    async mounted () {
      await this.loadUserPreferences()
      this.fetchLatestConversationMessages()
      await this.fetchLatestCommunityMembers()
      this.fetchLatestAnnouncements()
      await this.fetchUserTestimonials()
      this.finishedLoadingInitialData = true
      this.fetchTransitionPulse()
      this.fetchInnovationRadar()
      await this.fetchActorWeeklyTrend()
      await this.$store.dispatch(NOTIFICATION_ACTION_TYPES.FETCH_NOTIFICATIONS_LIST, {
        limit: this.$store.state.notifications.listData.fetchLimit,
        offset: 0,
      })
    },
    beforeUnmount () {
      this.$bus.off('updateUserProfile', this.resetUserObjects)
      this.$bus.off('productAddedViaOverlay', this.linkProductToParent)
    },
    mixins: [OnboardingMixin, TranslationsMixin, UiMixin, IntroJsMixin, CommunityMixin, SpottingAreaMixin],
    components: {
      PersonalHomeCommunityCard,
      PersonalHomeAnnouncementCard,
      PersonalHomeAnnouncementModal,
      ActorAvatar,
      SimplifiedAnnouncementPreview,
      SimpleArticlePreview,
      AnnouncementOrResourceCard,
      ProfileCardsSection,
      SimplifiedImageCard,
      ActorInfoCard,
      AnnouncementCard,
      ChallengeCardNew,
      ActorMiniCard,
      CommunityMemberCard,
      AvatarNew,
      Tabs,
      Avatar,
      Dropdown,
      ToggleButton
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";

  /* Primary-coloured link used for "View all" buttons */
  .ph-link-primary {
    color: var(--primary, #2563eb);

    :deep(svg) {
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }

    &:hover :deep(svg) {
      transform: translateX(3px);
    }
  }

  /* ── Rail (horizontal scroll on small screens) ────────────── */
  .ph-communities-rail {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.75rem;

    &--suggested {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  @media (max-width: 480px) {
    .ph-communities-rail {
      grid-template-columns: 1fr;
    }
  }

  /* ── Empty state icon wrap (animations + :deep override) ─── */
  .ph-empty-state__icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.85rem;
    color: #9ca3af;

    :deep(.svg-icon) {
      display: block;
      width: 22px;
      height: 22px;
      fill: #9ca3af;
    }

    &--pulse {
      animation: ph-fade-up 0.4s ease both, ph-icon-pulse 3s ease-in-out 0.6s infinite;
    }

    &--float {
      animation: ph-fade-up 0.4s ease both, ph-icon-float 4s ease-in-out 0.6s infinite;
    }

    &--bounce {
      animation: ph-fade-up 0.4s ease both, ph-icon-bounce 2.5s ease-in-out 0.6s infinite;
    }
  }

  /* Fade-up entrance animation for empty state containers/text */
  .ph-empty-anim {
    animation: ph-fade-up 0.4s ease both;
  }

  .ph-empty-anim-text {
    animation: ph-fade-up 0.4s 0.1s ease both;
  }

  /* ── Keyframes ────────────────────────────────────────────── */
  @keyframes ph-fade-up {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes ph-icon-pulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
    }
    50% {
      transform: scale(1.06);
      box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.08);
    }
  }

  @keyframes ph-icon-float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-5px);
    }
  }

  @keyframes ph-icon-bounce {
    0%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-6px);
    }
    60% {
      transform: translateY(-3px);
    }
  }


</style>
